import { useCallback } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Per-crew "last seen the chat" timestamp (unix seconds), stored per-pubkey +
 * per-crew in localStorage. Used only to draw a subtle "new messages" divider
 * when a member returns to a chat — no network cost, works for every transport.
 *
 * We deliberately keep this local-only: crew chat is casual and the divider is a
 * convenience, not a synced read-receipt. Nothing here leaves the device.
 */
export function useCrewChatRead(crewCoord: string | undefined) {
  const { user } = useCurrentUser();
  const key =
    user && crewCoord
      ? `crew-chat-last-seen:${user.pubkey}:${crewCoord}`
      : 'crew-chat-last-seen:anon';
  const [lastSeen, setLastSeen] = useLocalStorage<number>(key, 0);

  const markSeen = useCallback(
    (ts?: number) => {
      setLastSeen(ts ?? Math.floor(Date.now() / 1000));
    },
    [setLastSeen],
  );

  return { lastSeen, markSeen };
}
